import {
    AggregatorObject,
    EqualsFilter,
    InProductScheduleFilter,
    ProductFilterField,
    PurchaseRule,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    ReduceAggregatorOperand,
    RuleFilterItem,
} from "types";
import { createEqualsFilter } from "./RuleFilter";

export const getActionByTemplateId = (purchaseRule: PurchaseRule, templateId: string) => {
    const { actions } = purchaseRule;
    return actions.find((action) => action.template?.id === templateId);
};

export const getTagIdsFromPurchaseRule = (purchaseRule: PurchaseRule) => {
    return (
        purchaseRule.tags?.map((tag) => {
            return tag.id;
        }) ?? []
    );
};

export const getPurchaseRulesByTag = (tag: string, purchaseRules?: PurchaseRule[]) => {
    return (
        purchaseRules?.filter((purchaseRule) => {
            const tags = getTagIdsFromPurchaseRule(purchaseRule);
            return tags.includes(tag);
        }) ?? []
    );
};

export const getReturnValueAggregator = (value: number) => {
    return {
        object: AggregatorObject.RETURN_VALUE_AGGREGATOR,
        operand: value,
    };
};

export const getReturnFieldAggregator = (value: string) => {
    return {
        object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
        operand: value,
    };
};

export const getReduceAggregator = (
    value: ID,
    operand: string,
    itemFilterField: string,
    aggregatorField: string,
): ReduceAggregatorOperand => {
    return {
        object: AggregatorObject.REDUCE_AGGREGATOR,
        itemFilter: createEqualsFilter(value, itemFilterField),
        operand,
        itemAggregator: {
            object: AggregatorObject.RETURN_FIELD_AGGREGATOR,
            operand: aggregatorField,
        },
    };
};

export const getEqualFilter = (operand: string, field: string): EqualsFilter => {
    return {
        object: PurchaseRuleFilterObject.EQUALS_FILTER,
        field,
        operand,
        operator: PurchaseRuleFilterOperator.EQUALS,
    };
};

// Get all ProductIds as array from given list of PurchaseRules
export const getProductIdsFromPurchaseRules = (purchaseRules: PurchaseRule[]): string[] => {
    const productIds: string[] = [];
    if (purchaseRules) {
        for (const purchaseRule of purchaseRules) {
            productIds.push(...getProductIdsFromPurchaseRule(purchaseRule));
        }
    }

    return productIds;
};

// Get all ProductIds as array from given single PurchaseRule
export const getProductIdsFromPurchaseRule = (purchaseRule: PurchaseRule): string[] => {
    const productIds: string[] = [];
    const { filter } = purchaseRule;
    if (filter) {
        if (filter.object === "in_filter" && filter.field === ProductFilterField.PRODUCT) {
            // for in filter, check field is "product.id"
            productIds.push(...(filter.operand as string[]));
        } else if (filter.object === "or_filter" || filter.object === "and_filter") {
            // for all other filters check is any in_filter in filter.filters and field is "product.id"
            for (const filterType of filter.filters) {
                if (filterType.object === "in_filter" && filterType.field === ProductFilterField.PRODUCT) {
                    productIds.push(...(filterType.operand as string[]));
                }
            }
        }
    }

    return productIds;
};

// Get all PurchaseRules that has given productId
export const filterPurchaseRulesByProductId = (purchaseRules: PurchaseRule[], productId: ID): PurchaseRule[] => {
    return purchaseRules.filter((purchaseRule) => {
        // find all productIds for this Purchase Rule
        const productIds: string[] = getProductIdsFromPurchaseRule(purchaseRule);
        // check if given productId id exists
        return productIds.includes(productId);
    });
};

export const isInProductScheduleFilter = (filter: RuleFilterItem): filter is InProductScheduleFilter => {
    if (filter.object === PurchaseRuleFilterObject.IN_PRODUCT_SCHEDULE_FILTER) {
        return true;
    }

    return false;
};

export const getPurchaseRuleForExperience = (purchaseRules: PurchaseRule[], experienceId: ID) => {
    return purchaseRules.find(
        (purchaseRule) =>
            purchaseRule.meta?.product === experienceId || purchaseRule.meta?.subProductId === experienceId,
    );
};
